import React, { useState } from "react";
import { Box, Stack, Typography, IconButton, TextField } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";

import TaskRow from "./TaskRow";
import { COLORS, RADIUS } from "./ticketTheme";

export default function ProjectGroup({
  project,
  orgUsers,
  onAddTask,
  onUpdateTask,
  onMoveTask,
  onAssignTask,
  onDeleteTask,
  onRenameProject,
  onDeleteProject,
}) {
  const [expanded, setExpanded] = useState(true);
  const [name, setName] = useState(project.name);
  const [editingName, setEditingName] = useState(false);

  const tasks = project.tasks || [];

  const commitName = () => {
    setEditingName(false);
    const trimmed = name.trim();
    if (trimmed && trimmed !== project.name) onRenameProject(trimmed);
    else setName(project.name);
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <IconButton size="small" onClick={() => setExpanded((v) => !v)}>
          {expanded ? (
            <ExpandMoreIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
        </IconButton>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            bgcolor: project.color,
          }}
        />

        {editingName ? (
          <TextField
            autoFocus
            variant="standard"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
            InputProps={{ disableUnderline: true }}
            sx={{ "& input": { fontSize: 16, fontWeight: 700 } }}
          />
        ) : (
          <Typography
            variant="subtitle1"
            fontWeight={700}
            onClick={() => setEditingName(true)}
            sx={{ cursor: "text", color: COLORS.textPrimary }}
          >
            {project.name}
          </Typography>
        )}

        <Typography variant="body2" sx={{ color: COLORS.textDisabled }}>
          {tasks.length} {tasks.length === 1 ? "task" : "tasks"}
        </Typography>

        <Box sx={{ flex: 1 }} />

        <Box
          component="button"
          type="button"
          onClick={onAddTask}
          sx={{
            border: "none",
            bgcolor: "transparent",
            color: COLORS.accent,
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.4,
          }}
        >
          <AddIcon sx={{ fontSize: 15 }} /> Task
        </Box>

        <IconButton size="small" onClick={onDeleteProject}>
          <CloseIcon sx={{ fontSize: 16, color: COLORS.textDisabled }} />
        </IconButton>
      </Stack>

      {expanded && (
        <Box
          sx={{
            border: "1px solid",
            borderColor: COLORS.border,
            borderRadius: `${RADIUS.card}px`,
            bgcolor: COLORS.cardBg,
            overflow: "hidden",
          }}
        >
          <Stack
            direction="row"
            sx={{
              px: 2,
              py: 1,
              bgcolor: COLORS.pageBg,
              borderBottom: "1px solid",
              borderColor: COLORS.border,
            }}
          >
            {["Task", "Status", "Priority", "Due date", "Assigned"].map(
              (label, i) => (
                <Box
                  key={label}
                  sx={{
                    width: i === 0 ? undefined : [140, 120, 160, 130][i - 1],
                    flex: i === 0 ? "1 1 320px" : undefined,
                    minWidth: i === 0 ? 200 : undefined,
                    fontSize: 12,
                    fontWeight: 700,
                    color: COLORS.accent,
                    textTransform: "uppercase",
                    letterSpacing: 0.3,
                  }}
                >
                  {label}
                </Box>
              )
            )}
            <Box sx={{ width: 32 }} />
          </Stack>

          <Box sx={{ px: 2 }}>
            {tasks.length === 0 ? (
              <Typography
                variant="body2"
                sx={{ color: COLORS.textDisabled, py: 2 }}
              >
                No tasks yet — click{" "}
                <Box
                  component="span"
                  onClick={onAddTask}
                  sx={{
                    color: COLORS.accent,
                    cursor: "pointer",
                    fontWeight: 700,
                  }}
                >
                  + Task
                </Box>{" "}
                to add one.
              </Typography>
            ) : (
              tasks.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  orgUsers={orgUsers}
                  onUpdate={(changes) => onUpdateTask(task.id, changes)}
                  onMove={(newStatus) => onMoveTask(task.id, newStatus)}
                  onAssign={(uid) => onAssignTask(task.id, uid)}
                  onDelete={() => onDeleteTask(task.id)}
                />
              ))
            )}
            <Box
              component="button"
              type="button"
              onClick={onAddTask}
              sx={{
                border: "none",
                bgcolor: "transparent",
                color: COLORS.textDisabled,
                fontSize: 13,
                py: 1.25,
                cursor: "pointer",
                "&:hover": { color: COLORS.textSecondary },
              }}
            >
              + Add task
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
